import { ReactNode, useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import styles from './Tooltip.module.css';

interface CustomTooltipProps {
  children: ReactNode;
  targetId: string;
  placement?: 'top' | 'bottom';
}

const CustomTooltip = ({ children, targetId, placement = 'top' }: CustomTooltipProps) => {
  const [isVisible, setIsVisible] = useState(false);
  // The box is measured before it is placed, so it is painted only once it has a position;
  // otherwise it flashes at the page's top-left corner for a frame.
  const [isPositioned, setIsPositioned] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [actualPlacement, setActualPlacement] = useState<'top' | 'bottom'>(placement);
  const tooltipRef = useRef<HTMLDivElement>(null);
    
  useEffect(() => {
    const targetElement = document.getElementById(targetId);
    if (!targetElement) return;
    
    const calculatePosition = () => {
      const targetRect = targetElement.getBoundingClientRect();
      
      if (!tooltipRef.current) return;
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      
      let top, left;
      let isTop = placement === 'top';
      
      if (isTop) {
        top = targetRect.top - tooltipRect.height - 10;
        
        if (top < 10) {
          top = targetRect.bottom + 10;
          isTop = false;
        }
      } else {
        top = targetRect.bottom + 10;
        
        if (top + tooltipRect.height > window.innerHeight - 10 && targetRect.top > tooltipRect.height + 10) {
          top = targetRect.top - tooltipRect.height - 10;
          isTop = true;
        }
      }
      
      left = targetRect.left + (targetRect.width / 2) - (tooltipRect.width / 2);
      
      if (left < 10) left = 10;
      if (left + tooltipRect.width > window.innerWidth - 10) {
        left = window.innerWidth - tooltipRect.width - 10;
      }

      /*
       * The box is position: fixed, so viewport coordinates are what it needs. Adding the
       * page scroll here (as this once did) pushed it down by however far the board was
       * scrolled - and on a document-scrolled board every scroll event moved it again, so the
       * tooltip trembled and sat over the wrong row.
       */
      setPosition({ top, left });
      setActualPlacement(isTop ? 'top' : 'bottom');
      setIsPositioned(true);
    };
    
    const handleMouseEnter = () => {
      setIsVisible(true);
      setTimeout(calculatePosition, 10);
    };
    
    const handleMouseLeave = () => {
      setIsVisible(false);
      setIsPositioned(false);
    };

    const handleFocus = () => {
      setIsVisible(true);
      setTimeout(calculatePosition, 10);
    };

    const handleBlur = () => {
      setIsVisible(false);
      setIsPositioned(false);
    };
    
    targetElement.addEventListener('mouseenter', handleMouseEnter);
    targetElement.addEventListener('mouseleave', handleMouseLeave);
    targetElement.addEventListener('focus', handleFocus);
    targetElement.addEventListener('blur', handleBlur);
    
    window.addEventListener('scroll', calculatePosition);
    window.addEventListener('resize', calculatePosition);
    
    return () => {
      targetElement.removeEventListener('mouseenter', handleMouseEnter);
      targetElement.removeEventListener('mouseleave', handleMouseLeave);
      targetElement.removeEventListener('focus', handleFocus);
      targetElement.removeEventListener('blur', handleBlur);
      window.removeEventListener('scroll', calculatePosition);
      window.removeEventListener('resize', calculatePosition);
    };
  }, [targetId, placement]);
  
  if (!isVisible) {
    return null;
  }
  
  return createPortal(
    <div
      ref={tooltipRef}
      className={`${styles['tooltip-container']} ${styles[`tooltip-${actualPlacement}`]}`}
      style={{
        position: 'fixed',
        top: `${position.top}px`,
        left: `${position.left}px`,
        visibility: isPositioned ? 'visible' : 'hidden',
      }}
    >
      {children}
    </div>,
    document.body
  );
};

export default CustomTooltip;
